import React, { useState, useEffect } from 'react';

const StockSummaryReportPrint = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [params, setParams] = useState({});

  // Get URL parameters or use defaults
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const extractedParams = {
      sessionBranchId: urlParams.get('sessionBranchId') || '8',
      ddl_Branch: urlParams.get('ddl_Branch') || '8',
      ddl_category: urlParams.get('ddl_category') || '',
      ddl_subCategory: urlParams.get('ddl_subCategory') || '',
      ddl_innersubcategory: urlParams.get('ddl_innersubcategory') || '',
      ddl_deepinnersubcategory: urlParams.get('ddl_deepinnersubcategory') || '',
      txt_itemname: urlParams.get('txt_itemname') || '',
      txt_item: urlParams.get('txt_item') || '',
      txt_FromDate: urlParams.get('txt_FromDate') || '01/04/2022', 
      txt_ToDate: urlParams.get('txt_ToDate') || '19/08/2025',
      ddl_Stockwise: urlParams.get('ddl_Stockwise') || '',
      financeyearstartdate: urlParams.get('financeyearstartdate') || '01/04/2022'
    };
    setParams(extractedParams);
  }, []);

  // Sample data structure matching your API response
  const defaultReportData = {
    branchName: "Branch 8",
    fromDate: "01/04/2022",
    toDate: "19/08/2025",
    itemCategory: "Not Selected",
    totalItems: 5,
    grandTotal: {
      totalStock: 976,
      totalAmount: 5221600.00
    },
    items: [
      {
        sNo: 1,
        itemCode: "",
        itemName: "BUMPER",
        partNo: "01",
        uom: "Nos",
        closingStock: 488.00,
        price: 6700.00,
        amount: 3269600.00
      },
      {
        sNo: 2,
        itemCode: "041102002", 
        itemName: "WIND SHIELD",
        partNo: "02",
        uom: "Nos",
        closingStock: 488.00,
        price: 4000.00,
        amount: 1952000.00
      }
    ]
  };

  // Fetch data from API
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        
        const apiUrl = `https://www.nissi-india.com/api/stock_sumRpt`;
        
        console.log("API Params:", params);
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log("API Response:", data);
        
        if (!data.success) {
          throw new Error('API returned unsuccessful response');
        }

        // Transform API data to match component structure
        const apiData = data.rows || [];
        
        const transformedData = {
          success: data.success,
          branchName: apiData.length > 0 ? apiData[0].fld_branchName : "Branch 8",
          fromDate: params.txt_FromDate,
          toDate: params.txt_ToDate,
          itemCategory: "Not Selected",
          totalItems: apiData.length,
          items: apiData.map((item, index) => ({
            sNo: index + 1,
            itemCode: item.fld_itemcode,
            itemName: item.fld_itemname,
            partNo: item.fld_itempartno || '',
            uom: item.fld_uomname,
            closingStock: parseFloat(item.ClosingStock || 0),
            price: parseFloat(item.Price1 || 0),
            amount: parseFloat(item.Amount || 0)
          })),
          grandTotal: {
            totalStock: apiData.reduce((sum, item) => sum + parseFloat(item.ClosingStock || 0), 0),
            totalAmount: apiData.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0)
          }
        };

        setReportData(transformedData);
        setError(null);
        
      } catch (err) {
        console.error('Error fetching stock summary report data:', err);
        setError(err.message);
        setReportData(defaultReportData);
      } finally {
        setLoading(false);
      }
    };

    if (Object.keys(params).length > 0) {
      fetchReportData();
    }
  }, [params]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value).replace('₹', '₹ ');
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  if (loading) {
    return (
      <div className="max-w-full mx-auto p-6 bg-white font-sans text-sm">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading stock summary report...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="max-w-full mx-auto p-6 bg-white font-sans text-sm">
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-red-600">
            <p className="text-lg font-semibold mb-2">Error Loading Report</p>
            <p>{error || "Failed to load stock summary report"}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto p-6 bg-white font-sans text-sm">
      {/* API Error Warning */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded no-print">
          <p className="text-sm">
            <strong>Note:</strong> Using sample data due to API error. ({error})
          </p>
        </div>
      )}

      {/* Outer Border */}
      <div className="border-4 border-black p-4 print:border-black">
        
        {/* Header Section */}
        <div className="border-2 border-black mb-4 print:border-black">
          <div className="flex items-start p-4">
            {/* Logo Section */}
            <div className="w-32 h-20 border-2 border-black mr-6 flex items-center justify-center bg-white">
              <div className="text-center">
                <div className="w-12 h-8 bg-black mx-auto mb-1 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">LOGO</span>
                </div>
                <div className="text-xs font-bold">NISSI INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>
            
            {/* Company Info */}
            <div className="flex-1 text-center">
              <h1 className="text-xl font-bold mb-1">Nissi Infotech</h1>
              <p className="text-sm mb-1">NISSI</p>
              <p className="text-sm font-bold mb-1">CIVIL CONSTRUCTION</p>
              <p className="text-xs mb-2">
                3/42-12, Nagappa colony, kavundampai,644003,Tamil Nadu,India,<br/>
                Phone No :9003744644 infraconsoft-implementation@nissiinfotech.in
              </p>
              <div className="border-2 border-black p-1 inline-block">
                <h2 className="text-lg font-bold">STOCK SUMMARY REPORT</h2>
              </div>
            </div>

            {/* Screen Info */}
            <div className="text-right text-xs">
              <p>INFRA SCREEN : A-59/2</p>
            </div>
          </div>
        </div>

        {/* Report Parameters */}
        <div className="mb-4 text-sm">
          <div className="flex justify-between items-center mb-2">
            <div>
              <span className="font-bold">Master Head -</span> {reportData.branchName}
            </div>
            <div>
              <span className="font-bold">From Date -</span> {reportData.fromDate}
            </div>
            <div>
              <span className="font-bold">To Date -</span> {reportData.toDate}
            </div>
          </div>
        </div>

        {/* Main Table */}
        <div className="border-2 border-black mb-4 print:border-black">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-100 print:bg-white">
                <th className="border border-black px-2 py-2 text-center print:border-black">SNo</th>
                <th className="border border-black px-2 py-2 text-center print:border-black">Item Code</th>
                <th className="border border-black px-2 py-2 text-center print:border-black">Item Name</th>
                <th className="border border-black px-2 py-2 text-center print:border-black">Part No</th>
                <th className="border border-black px-2 py-2 text-center print:border-black">UOM</th>
                <th className="border border-black px-2 py-2 text-center print:border-black">Cls. Stock</th>
                <th className="border border-black px-2 py-2 text-center print:border-black">Price</th>
                <th className="border border-black px-2 py-2 text-center print:border-black">Amount</th>
              </tr>
            </thead>
            <tbody>
              {reportData.items.map((item) => (
                <tr key={item.sNo}>
                  <td className="border border-black px-2 py-1 text-center print:border-black">{item.sNo}</td>
                  <td className="border border-black px-2 py-1 text-center print:border-black">{item.itemCode}</td>
                  <td className="border border-black px-2 py-1 text-left print:border-black">{item.itemName}</td>
                  <td className="border border-black px-2 py-1 text-center print:border-black">{item.partNo}</td>
                  <td className="border border-black px-2 py-1 text-center print:border-black">{item.uom}</td>
                  <td className="border border-black px-2 py-1 text-right print:border-black">{formatNumber(item.closingStock)}</td>
                  <td className="border border-black px-2 py-1 text-right print:border-black">{formatCurrency(item.price)}</td>
                  <td className="border border-black px-2 py-1 text-right print:border-black">{formatCurrency(item.amount)}</td>
                </tr>
              ))}

              {/* Total Row */}
              <tr className="bg-gray-100 print:bg-white font-bold">
                <td className="border border-black px-2 py-1 text-center print:border-black" colSpan="5"></td>
                <td className="border border-black px-2 py-1 text-right print:border-black">{formatNumber(reportData.grandTotal.totalStock)}</td>
                <td className="border border-black px-2 py-1 text-right print:border-black"></td>
                <td className="border border-black px-2 py-1 text-right print:border-black">{formatCurrency(reportData.grandTotal.totalAmount)}</td>
              </tr>

              {/* No Data Message */}
              {reportData.items.length === 0 && (
                <tr>
                  <td className="border border-black px-2 py-8 text-center print:border-black" colSpan="8">
                    <div className="text-gray-500">
                      No stock records found for the specified criteria
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="border-2 border-black print:border-black">
          <div className="p-2 text-right text-xs">
            <span className="font-bold">Page 1 of 1</span>
          </div>
          <div className="border-t-2 border-black p-2 text-xs print:border-black">
            Report generated by ERP-Software - www.infraconsoft.com owned by <span className="font-bold">www.nissiinfotech.com</span>
          </div>
        </div>

      </div>

      {/* Control Buttons */}
      <div className="mt-6 text-center no-print">
        <button
          onClick={() => window.print()}
          className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors mr-4"
        >
          Print Report
        </button>
        <button
          onClick={() => window.location.reload()}
          className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700 transition-colors mr-4"
        >
          Refresh Data
        </button>
        <button
          onClick={() => window.close()}
          className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition-colors"
        >
          Close Window
        </button>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            margin: 0;
            padding: 10mm;
            background: white !important;
          }
          * {
            color: black !important;
            background: white !important;
            border-color: black !important;
          }
          .print\\:border-black {
            border-color: black !important;
          }
          .print\\:bg-white {
            background-color: white !important;
          }
          table {
            font-size: 7px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default StockSummaryReportPrint;